import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Zap, Loader2, AlertCircle, ShieldCheck, ArrowLeft } from 'lucide-react';

const ADMIN_BASE = "/admin";

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get redirect path from location state, default to admin dashboard
  const from = location.state?.from?.pathname || ADMIN_BASE;

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(username, password);
    
    setIsLoading(false);
    
    if (result.success) {
      if (result.mfaRequired) {
        // MFA is required - show MFA input
        setMfaRequired(true);
        setMfaToken(result.mfaToken);
      } else {
        // No MFA - proceed to dashboard
        navigate(from, { replace: true });
      }
    } else {
      setError(result.error);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await verifyMfa(mfaToken, mfaCode);
    
    setIsLoading(false);
    
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
      setMfaCode('');
    }
  };

  const handleBackToPassword = () => {
    setMfaRequired(false);
    setMfaToken('');
    setMfaCode('');
    setError('');
    setPassword('');
  };

  // MFA verification screen
  if (mfaRequired) {
    return (
      <div className="login-container">
        <Card className="login-card">
          <CardHeader className="text-center">
            <div className="login-logo">
              <div className="login-logo-icon bg-green-500/10">
                <ShieldCheck size={28} className="text-green-500" />
              </div>
            </div>
            <CardTitle className="text-2xl">Two-Factor Authentication</CardTitle>
            <CardDescription>
              Enter the 6-digit code from your authenticator app
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleMfaSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <div className="login-error">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="mfaCode">Authentication Code</Label>
                <Input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  required
                  autoComplete="one-time-code"
                  disabled={isLoading}
                  className="text-center text-2xl tracking-widest font-mono"
                  data-testid="mfa-code-input"
                />
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Or use a backup code if you do not have access to your authenticator
                </p>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-2">
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading || mfaCode.length < 6}
                data-testid="mfa-verify-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>
              <Button 
                type="button"
                variant="ghost"
                className="w-full"
                onClick={handleBackToPassword}
                disabled={isLoading}
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to login
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    );
  }

  // Password login screen
  return (
    <div className="login-container">
      <Card className="login-card">
        <CardHeader className="text-center">
          <div className="login-logo">
            <div className="login-logo-icon">
              <Zap size={28} />
            </div>
          </div>
          <CardTitle className="text-2xl">LeadForge</CardTitle>
          <CardDescription>Sign in to your admin account</CardDescription>
        </CardHeader>
        <form onSubmit={handlePasswordSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="login-error">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                disabled={isLoading}
                data-testid="username-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link 
                  to={`${ADMIN_BASE}/forgot-password`}
                  className="text-sm text-blue-600 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={isLoading}
                data-testid="password-input"
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading}
              data-testid="login-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default LoginPage;
